/* eslint-disable @typescript-eslint/no-explicit-any */
import cn from 'classnames'
import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { ITEM_HEIGHT } from '@/components/Properties/tableProperties/constants'
import { LightTableColumn } from '@/types'

import { getRowValue } from '../helpers'
import { RowCallback } from '../types'
import Cell from './Cell'
import { RowOrderer } from './RowOrderer'

type RowProps<T> = {
	$isDragging?: boolean,
	columns: { column: LightTableColumn<T>; style: React.CSSProperties }[],
	dragArrowVisible?: boolean,
	isDragOver?: boolean,
	isExpandable?: boolean,
	isExpanded?: boolean,
	isRowOrderable: { down?: boolean; enabled: boolean, up?: boolean; },

	key?: string,
	onDragChanged?: ($isDragging: boolean) => void,
	onDragOver?: (index: number) => void,
	onRowClick?: (row: T) => void,
	onRowDrop?: (dropIndex: number) => void,
	onRowExpand?: (row: T, index: number) => void,

	onRowOrderChanged?: (oldIndex: number, newIndex: number) => void,
	position?: number,
	row: T,
	rowIndex: number,
	scrollLeft?: number,
	trClassName?: string | RowCallback<T, string>,
	trStyle?: React.CSSProperties | RowCallback<T, React.CSSProperties>,
	width?: string
}

const Row = <T,>({
	row,
	rowIndex,
	trClassName,
	isExpandable,
	isExpanded,
	position,
	trStyle,
	onRowClick,
	columns,
	onRowExpand,
	width,
	scrollLeft = 0,
	isRowOrderable,
	onRowOrderChanged,
	onDragChanged,
	onRowDrop,
	isDragOver,
	$isDragging,
	onDragOver,
	dragArrowVisible = true,
}: RowProps<T>) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const [isRowDragging, setIsRowDragging] = useState(false)

	const handleOrderDragStart = (e: React.DragEvent<HTMLDivElement>) => {
		onDragChanged?.(true)

		if (containerRef.current) {
			e.dataTransfer.setDragImage(containerRef.current, 0, 0)
		}

		setIsRowDragging(true)
	}

	const handleOrderDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
		onDragChanged?.(false)

		setIsRowDragging(false)

		e.preventDefault()
	}

	const handleOrderDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		if (
			onDragOver &&
			isRowOrderable.enabled &&
			(isRowOrderable.up || isRowOrderable.down)
		) {
			onDragOver(rowIndex)
		}

		e.stopPropagation()
		e.preventDefault()
	}

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		if (
			onRowDrop &&
			isRowOrderable.enabled &&
			(isRowOrderable.up || isRowOrderable.down)
		) {
			onRowDrop(rowIndex)
		}

		e.preventDefault()
	}

	return (
		<Container
			$isRowDragging={isRowDragging}
			$isDragOver={isDragOver && $isDragging}
			role="row"
			onDrop={handleDrop}
			onDragOver={handleOrderDragOver}
			ref={containerRef}
			className={cn(
				'div-tr',
				getRowValue(trClassName, row, rowIndex),
				isExpandable && (isExpanded ? 'expanded' : 'collapsed'),
			)}
			style={{
				...(position !== undefined && {
					position: 'absolute',
					transform: `translateY(${position}px)`,
					minWidth: width,
				}),
				...getRowValue(trStyle, row, rowIndex),
			}}
			onClick={onRowClick && (() => onRowClick(row))}
		>
			{isRowOrderable.enabled && onRowOrderChanged && (
				<RowOrderer
					dragArrowVisible={dragArrowVisible}
					onOrderDragStart={handleOrderDragStart}
					onOrderDragEnd={handleOrderDragEnd}
					onRowOrderChanged={onRowOrderChanged}
					index={rowIndex}
					isRowOrderable={isRowOrderable}
				/>
			)}
			{columns.map(({ column, style }, index) => {
				const value = column.field ? row[column.field] : null

				return (
					<Cell
						key={index.toString()}
						column={column}
						style={style}
						value={value}
						row={row}
						rowIndex={rowIndex}
						isExpandable={isExpandable}
						onRowExpand={onRowExpand}
						scrollLeft={scrollLeft}
					/>
				)
			})}
		</Container>
	)
}

interface ContainerProps {
	$isDragOver?: boolean
	$isRowDragging: boolean
}

const Container = styled.div<ContainerProps>`
	display: flex;
	align-items: stretch;
	height: ${ITEM_HEIGHT}px;
	position: relative;

	${(props: ContainerProps) =>
		props.$isRowDragging &&
		css`
			background: #f0f0ff;
		`}

	${(props: ContainerProps) =>
		props.$isDragOver &&
		!props.$isRowDragging &&
		css`
			background: #f0f0f0;
		`}
`

export default React.memo(Row) as typeof Row
